"use client";

import { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import Loading from "./Loading";

/** ロード画面の印を出し始めるまでの間。すぐ出せる画面では印を出さない */
const SHOW_MS = 150;
/** 中の絵を待つ上限。取れない絵が在っても先へ進む */
const IMAGE_MS = 4000;
/** 申し出を待つ上限。下りない申し出が在っても、過ぎたら中身を出す */
const HOLD_MS = 10000;

/** 中の画面が「まだ出せない」と申し出る口。返るのは申し出を下ろす関数 */
const HoldContext = createContext<(() => () => void) | null>(null);

/** 一番近いロード画面に、busy の間だけ待ってもらう */
export function useGateHold(busy: boolean) {
  const hold = useContext(HoldContext);
  useLayoutEffect(() => {
    if (!hold || !busy) return;
    return hold();
  }, [hold, busy]);
}

/** useGateHold の部品の形。ロード画面の中に置く */
export function GateHold({ busy }: { busy: boolean }) {
  useGateHold(busy);
  return null;
}

/**
 * ロード画面。画面を切り替えるとき、中身が出せるようになるまで読み込み中の印を出す。
 * 中身は裏で組み、中の画面の申し出（useGateHold・GateHold）が全て下り、中の絵を取り終えたら出す。
 * 1 度出した後は出したままにする。切り替えるたびに key を替えて組み直す。
 *   variant  screen は画面全体、area は親の枠の空きを埋める、inline はその場に出す
 *   open     true は待たずに最初から出す
 */
export default function Gate({
  children,
  variant = "area",
  label,
  open = false,
}: {
  children: React.ReactNode;
  variant?: "screen" | "area" | "inline";
  label?: string;
  open?: boolean;
}) {
  const [holds, setHolds] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(open);
  const [late, setLate] = useState(false);
  const [forced, setForced] = useState(false);
  const body = useRef<HTMLDivElement | null>(null);

  const hold = useCallback(() => {
    setHolds((count) => count + 1);
    return () => setHolds((count) => count - 1);
  }, []);

  useLayoutEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => setLate(true), SHOW_MS);
    const limit = setTimeout(() => setForced(true), HOLD_MS);
    return () => {
      clearTimeout(timer);
      clearTimeout(limit);
    };
  }, []);

  // 申し出が全て下りたら、中の絵を待ってから出す
  useLayoutEffect(() => {
    if (shown || !mounted || (holds > 0 && !forced)) return;
    const images = Array.from(body.current?.querySelectorAll("img") ?? []).filter((image) => !image.complete);
    if (images.length === 0) {
      setShown(true);
      return;
    }
    let alive = true;
    const done = () => {
      if (alive) setShown(true);
    };
    const timer = setTimeout(done, IMAGE_MS);
    Promise.allSettled(images.map((image) => image.decode())).then(done);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [shown, mounted, holds, forced]);

  return (
    <HoldContext.Provider value={hold}>
      {shown ? null : <div className={`ui-gate ui-gate-${variant}`}>{late ? <Loading label={label} /> : null}</div>}
      <div ref={body} className={shown ? "ui-gate-body" : "ui-gate-body ui-gate-hidden"}>
        {children}
      </div>
    </HoldContext.Provider>
  );
}
